import { useEffect, useRef, useState } from "react";
import { api, formatApiError, uploadFiles } from "@/lib/api";
import { SignedImg } from "@/components/shared/SignedImg";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ReviewPanel } from "@/components/shared/ReviewPanel";
import { ApproveButton } from "@/components/shared/ApproveButton";
import { Avatar } from "@/components/shared/Avatar";
import { TimeStepper } from "@/components/shared/TimeStepper";
import { celebrate } from "@/lib/celebrate";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter } from "@/components/ui/alert-dialog";
import { formatHours, formatCZK, formatDate, formatTime } from "@/lib/format";
import { roleLabel } from "@/lib/roles";
import { PresenceMap } from "@/components/shared/PresenceMap";
import { PhotoPinViewer } from "@/components/defects/PhotoPinViewer";
import { DefectTaskForm } from "@/components/defects/DefectTaskForm";
import { shareToWhatsApp } from "@/components/gallery/ShareSheet";
import { useAuth, canLeadTasks } from "@/context/AuthContext";
import {
  Check, Trash2, MoreVertical, Eye, EyeOff, Camera, ListChecks, Package, Pencil, MapPin,
  ImagePlus, MessageSquare, Send, AlertTriangle, BookOpen, Clock, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { autoPill } from "@/components/shared/Pill";

const pragueDate = (iso) => iso ? new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Prague", year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(iso)) : "";
const pragueTime = (iso) => iso ? new Intl.DateTimeFormat("cs-CZ", { timeZone: "Europe/Prague", hour: "2-digit", minute: "2-digit", hour12: false }).format(new Date(iso)) : "";
const toMin = (t) => { const [h, m] = (t || "0:0").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
const hhmm = (t) => (t || "").replace(/^0(\d):/, "$1:");          // 08:07 → 8:07 (jen na čtení)
const dayLabel = (iso) => (iso ? new Intl.DateTimeFormat("cs-CZ", { weekday: "short", day: "numeric", month: "numeric", timeZone: "Europe/Prague" }).format(new Date(`${iso}T12:00:00Z`)) : "");
const WHY = ["Zapomněl se odhlásit", "Přišel později", "Odešel dřív", "Podle deníku", "Úklid po směně"];
const BREAKS = [0, 15, 30, 45, 60];

// Poznámky nahoře (dřív štítky varování 161) — stejné pořadí, ale s vysvětlením.
function Note({ tone = "info", icon: Icon = AlertTriangle, title, children, testid }) {
  const box = { bad: "bg-destructive/10", warn: "bg-amber-500/10", info: "bg-background dark:bg-secondary" }[tone];
  const ic = { bad: "text-destructive", warn: "text-amber-600 dark:text-amber-400", info: "text-muted-foreground" }[tone];
  return (
    <div className={`flex gap-2.5 items-start rounded-[16px] px-3.5 py-3 text-[13.5px] ${box}`} data-testid={testid}>
      <Icon size={17} className={`shrink-0 mt-0.5 ${ic}`} />
      <div className="min-w-0"><b className="block font-bold">{title}</b>{children && <span className="text-muted-foreground break-words">{children}</span>}</div>
    </div>
  );
}

function Section({ icon: Icon, title, action, children, testid, ...rest }) {
  return (
    <section className="space-y-2.5 min-w-0" data-testid={testid} {...rest}>
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-[14.5px] font-bold">{Icon && <Icon size={17} className="text-brand shrink-0" />}{title}</h3>
        {action}
      </div>
      {children}
    </section>
  );
}

// Krok osy Příchod → Přestávka → Odchod → Schválení (Účtenka, 422)
function Step({ dot, label, sub, value, onEdit, open, editKey, children, last }) {
  const dotCls = { ok: "bg-success border-success", bad: "bg-destructive border-destructive", now: "bg-card border-brand ring-4 ring-brand/15", idle: "bg-card border-muted-foreground/50" }[dot];
  return (
    <div className={`relative pl-7 py-3 ${last ? "" : "border-b border-border/60"}`} data-testid={`shift-step-${editKey}`}>
      {!last && <span className="absolute left-[6px] top-[34px] -bottom-[14px] w-[2px] bg-border" aria-hidden="true" />}
      <span className={`absolute left-0 top-[17px] h-[14px] w-[14px] rounded-full border-2 z-[1] ${dotCls}`} aria-hidden="true" />
      <div className="flex items-center justify-between gap-3 min-w-0">
        <div className="min-w-0"><b className="block text-[14.5px] font-bold">{label}</b><div className="text-[12.5px] text-muted-foreground">{sub}</div></div>
        <div className="flex items-center gap-2 shrink-0 text-lg font-black tabular-nums whitespace-nowrap">
          {value}
          {onEdit && (
            <button type="button" onClick={onEdit} aria-expanded={open} aria-label={`Upravit ${label.toLowerCase()}`} data-testid={`shift-edit-${editKey}`}
              className={`h-8 w-8 rounded-full border grid place-items-center transition-colors ${open ? "bg-foreground text-background border-foreground" : "bg-card border-border text-muted-foreground hover:text-foreground"}`}>
              {open ? <Check size={15} /> : <Pencil size={14} />}
            </button>
          )}
        </div>
      </div>
      {open && <div className="mt-3 flex flex-wrap items-center gap-2.5">{children}</div>}
    </div>
  );
}

// JEDNO okno směny (161 → 422 „Tvůj mix“): z Přehledu (fronta, nav = { index, count, onGo }), Docházky,
// šichtovnice, karty zakázky i deníku. Nahoře vlevo Schválit (jako úkoly a faktury), vpravo ⋮ (jen smazání)
// a ↑ ↓ ✕. Velké číslo hodin, záložky Směna | Místo. Schválená směna je zamčená — časy jdou měnit až po
// zrušení schválení (druhé klepnutí na „Schváleno“). Obsah z /attendance/{sid}/review.
export function ShiftReviewDialog({ sid, mode = "approve", canSeeMoney = false, onClose, onChanged, onApproval, nav = null }) {
  const [id, setId] = useState(sid);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ in: "", out: "", brk: "", report: "" });
  const [edit, setEdit] = useState(null);           // který krok osy je otevřený: in | brk | out
  const [repEdit, setRepEdit] = useState(false);
  const [why, setWhy] = useState(null);
  const [whyText, setWhyText] = useState("");
  const [needWhy, setNeedWhy] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [comments, setComments] = useState([]);
  const [cm, setCm] = useState("");
  const [delPhoto, setDelPhoto] = useState(null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tab, setTab] = useState("shift");
  const { user } = useAuth() || {};   // bez AuthProvideru (testy) = bez závad
  const [drafts, setDrafts] = useState([]);
  const [pinView, setPinView] = useState(null);     // { start, focusDraft }
  const defRef = useRef(null);
  const fileRef = useRef(null);
  const [defFlash, setDefFlash] = useState(false);
  const showDefectForm = () => {
    setPinView(null);
    setTimeout(() => {
      defRef.current?.scrollIntoView({ block: "center", behavior: "smooth" });
      setDefFlash(true);
      setTimeout(() => setDefFlash(false), 1600);
    }, 260);
  };
  const origRef = useRef({ in: "", out: "", brk: "", report: "" });

  useEffect(() => { setId(sid); }, [sid]);

  const resetEdits = () => { setEdit(null); setRepEdit(false); setWhy(null); setWhyText(""); setNeedWhy(false); };
  const loadReview = (rid, quiet = false) => {
    if (!quiet) setLoading(true);
    setMenuOpen(false);
    api.get(`/attendance/${rid}/review`)
      .then((r) => {
        const d = r.data;
        setData(d);
        const init = { in: pragueTime(d.check_in), out: pragueTime(d.check_out), brk: d.break_minutes ?? "", report: d.report || "" };
        origRef.current = init;
        setForm(init);
        setPhotos(d.photos || []);
        setComments(d.comments || []);
        resetEdits();
      })
      .catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); onClose?.(); })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Každá směna začíná čistě — nic z předchozího člověka (161 bug: fotka zůstávala).
    setDelPhoto(null); setConfirmDel(false); setMenuOpen(false); setTab("shift"); setCm("");
    setDrafts([]); setPinView(null); resetEdits();
    if (!id) { setData(null); setPhotos([]); setComments([]); return; }
    setData(null); setPhotos([]); setComments([]);
    loadReview(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!sid) return null;

  const approved = data?.approval_status === "approved";
  const finished = !!data?.check_out;
  const daySheet = !!data?.day_id;                    // směna z denního výkazu se schvaluje celým výkazem
  const editable = !!data && finished && !approved && !daySheet;
  const o = origRef.current;
  const dirty = editable && (form.in !== o.in || form.out !== o.out || String(form.brk) !== String(o.brk) || form.report !== o.report);
  const reason = [why, whyText.trim()].filter(Boolean).join(" · ");

  // Živý přepočet hodin a mzdy podle rozepsaných časů.
  const spanOf = (a, b) => { let s = toMin(b) - toMin(a); if (s < 0) s += 1440; return s; };
  const span = spanOf(form.in, form.out);
  const brkMin = form.brk === "" ? (span > 360 ? 30 : 0) : Math.max(0, Number(form.brk) || 0);
  const liveHours = finished ? Math.max(0, span - brkMin) / 60 : (data?.hours || 0);
  const rate = data && data.cost != null && data.hours ? data.cost / data.hours : null;
  const liveCost = rate != null ? Math.round(rate * liveHours) : null;
  const others = data?.day_others || [];
  const dayTotal = liveHours + others.reduce((a, x) => a + (x.hours || 0), 0);
  // původní hodiny z původních časů (ne z uložených hodin — ty můžou být zaokrouhlené jinak)
  const origHours = Math.max(0, spanOf(o.in, o.out) - (o.brk === "" ? (spanOf(o.in, o.out) > 360 ? 30 : 0) : Number(o.brk) || 0)) / 60;
  const changes = [];
  if (dirty) {
    if (form.in !== o.in) changes.push(`Příchod ${hhmm(o.in)} → ${hhmm(form.in)}`);
    if (form.out !== o.out) changes.push(`Odchod ${hhmm(o.out)} → ${hhmm(form.out)}`);
    if (String(form.brk) !== String(o.brk)) {
      const b0 = o.brk === "" ? (spanOf(o.in, o.out) > 360 ? 30 : 0) : Number(o.brk) || 0;
      changes.push(`Přestávka ${b0} → ${brkMin} min`);
    }
    if (form.report !== o.report) changes.push("Popis práce");
  }

  const buildISO = () => ({
    check_in: `${pragueDate(data.check_in)}T${form.in}`,
    check_out: `${pragueDate(data.check_out) || pragueDate(data.check_in)}T${form.out}`,
  });
  const payload = () => ({ ...buildISO(), break_minutes: form.brk === "" ? null : Number(form.brk), report: form.report, note: reason || undefined });
  const askWhy = () => { setNeedWhy(true); toast.error(`Napište důvod úpravy — ${data.user.name} ho uvidí.`); };

  // 358/422: Schválit vlevo nahoře — okno zůstane, tlačítko zezelená, vyletí konfety
  const approve = async (e) => {
    const from = e?.currentTarget;
    if (dirty && !reason) return askWhy();
    setBusy(true);
    try {
      if (daySheet) await api.post(`/daysheet/${data.day_id}/approve`, {});
      else await api.post(`/attendance/${data.id}/approve`, payload());
      onChanged?.(); onApproval?.(true);
      setData((d) => d && { ...d, approval_status: "approved" }); celebrate(from); loadReview(data.id, true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  // Schváleno omylem — druhé klepnutí vrátí směnu ke schválení (a tím ji odemkne)
  const unapprove = async () => {
    setBusy(true);
    try {
      await api.post(daySheet ? `/daysheet/${data.day_id}/unapprove` : `/attendance/${data.id}/unapprove`);
      setData((d) => d && { ...d, approval_status: "pending" }); onChanged?.(); onApproval?.(false); loadReview(data.id, true);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const saveEdits = async () => {
    if (!reason) return askWhy();
    setBusy(true);
    try {
      await api.patch(`/attendance/${data.id}`, payload());
      toast.success("Uloženo. Pracovník uvidí důvod u své směny."); onChanged?.(); loadReview(data.id, true);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const discard = () => { setForm({ ...origRef.current }); resetEdits(); };

  const delRecord = async () => {
    setBusy(true);
    try { await api.delete(`/attendance/${data.id}`); toast.success(data.source === "diary" ? "Odstraněno z deníku." : "Záznam smazán."); onChanged?.(); onClose?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); setConfirmDel(false); }
  };

  const toggleEye = async (p) => {
    const to = p.status !== "approved";
    setPhotos((ps) => ps.map((x) => x.id === p.id ? { ...x, status: to ? "approved" : "pending" } : x));
    try { await api.post("/diary/photos/approve", { photo_ids: [p.id], approved: to }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setPhotos((ps) => ps.map((x) => x.id === p.id ? { ...x, status: p.status } : x)); }
  };
  const approveAllPhotos = async () => {
    const ids = photos.filter((p) => p.status !== "approved").map((p) => p.id);
    if (!ids.length) return;
    setPhotos((ps) => ps.map((x) => ({ ...x, status: "approved" })));
    try { await api.post("/diary/photos/approve", { photo_ids: ids, approved: true }); toast.success("Fotky přidány do deníku."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id, true); }
  };
  const doDelPhoto = async () => {
    const p = delPhoto; setDelPhoto(null);
    setPhotos((ps) => ps.filter((x) => x.id !== p.id));
    try { await api.delete(`/photos/${p.id}`); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id, true); }
  };
  // 422: vedoucí přidá fotky ke směně (výběrem nebo přetažením) — do deníku jdou skryté, pustí je okem
  const addPhotos = async (files) => {
    const list = Array.from(files || []).filter((f) => /^image\//.test(f.type) || /\.(heic|heif)$/i.test(f.name || ""));
    if (!list.length) return toast.error("Nahrát jde jen fotka (JPG, PNG nebo HEIC).");
    setUploading(list.length);
    try {
      const { urls, failed } = await uploadFiles(list, { onEach: (done, total) => setUploading(total - done) });
      if (failed.length) toast.error(`${failed.length} ${failed.length === 1 ? "fotku" : "fotky"} se nepodařilo nahrát.`);
      if (urls.length) {
        const r = await api.post(`/attendance/${data.id}/photos`, { urls });
        setPhotos(r.data.photos || []);
        toast.success(urls.length === 1 ? "Fotka přidaná, zatím skrytá z deníku." : "Fotky přidané, zatím skryté z deníku.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUploading(0); if (fileRef.current) fileRef.current.value = ""; }
  };
  const sendComment = async () => {
    const text = cm.trim();
    if (!text) return;
    try {
      const r = await api.post(`/attendance/${data.id}/comments`, { text });
      setComments(r.data.comments || []); setCm("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const flags = data?.flags || [];
  const canPinDefects = !!data && canLeadTasks(user) && !!data.project?.id && photos.length > 0;
  const photoUrls = photos.map((p) => p.url);
  const gps = data?.gps || {};
  const ci = gps.checkin || {}, co = gps.checkout || {};
  const name = data?.user?.name || "";

  const menu = (
    <div className="relative shrink-0">
      <button type="button" onClick={() => setMenuOpen((v) => !v)} aria-label="Další akce" aria-expanded={menuOpen} data-testid="shift-review-menu"
        className="h-9 w-9 grid place-items-center rounded-[10px] border border-border text-muted-foreground hover:text-foreground"><MoreVertical size={17} /></button>
      {menuOpen && (
        <div className="absolute right-0 mt-1 w-52 rounded-[12px] border border-border bg-popover shadow-lg z-20 p-1" onMouseLeave={() => setMenuOpen(false)}>
          <button type="button" onClick={() => { setMenuOpen(false); setConfirmDel(true); }} data-testid="shift-review-delete-menu"
            className="w-full text-left px-3 py-2 rounded-[8px] text-sm font-semibold text-destructive hover:bg-destructive/10 flex items-center gap-2">
            <Trash2 size={15} /> {data?.source === "diary" ? "Odstranit z deníku" : "Smazat záznam"}
          </button>
        </div>
      )}
    </div>
  );

  // Poznámky: varování ze směny + kdo co kdy upravil a proč
  const corrText = (c) => {
    const a = c.after || {}, b = c.before || {}, out = [];
    if ("check_in" in a) out.push(`příchod ${hhmm(pragueTime(b.check_in))} → ${hhmm(pragueTime(a.check_in))}`);
    if ("check_out" in a) out.push(`odchod ${hhmm(pragueTime(b.check_out)) || "—"} → ${hhmm(pragueTime(a.check_out)) || "—"}`);
    if ("break_minutes" in a) out.push(`přestávka ${b.break_minutes ?? "auto"} → ${a.break_minutes ?? "auto"} min`);
    if ("report" in a) out.push("popis práce");
    if ("rate_override" in a) out.push("sazba");
    return out.join(", ");
  };
  const notes = data ? [
    flags.includes("out_of_radius") && <Note key="r" tone="bad" icon={MapPin} title="Mimo okruh stavby" testid="flag-out_of_radius">{`${gps.distance_m ?? "?"} m od stavby, okruh je ${gps.radius_m || 300} m.`}</Note>,
    flags.includes("auto_closed") && <Note key="a" tone="bad" icon={Clock} title={`Odhlásil systém${data.auto_closed_at ? ` v ${hhmm(pragueTime(data.auto_closed_at))}` : ""}`} testid="flag-auto_closed">Pracovník se sám neodhlásil.</Note>,
    flags.includes("overtime_beyond_auto") && <Note key="o" tone="bad" title="Přesčas nad rámec" testid="flag-overtime_beyond_auto" />,
    flags.includes("short") && <Note key="s" tone="warn" icon={Clock} title={`Krátká směna · ${Math.round((data.hours || 0) * 60)} min`} testid="flag-short" />,
    flags.includes("no_gps") && <Note key="g" tone="warn" icon={MapPin} title="Bez GPS" testid="flag-no_gps">Telefon polohu neposlal.</Note>,
    flags.includes("no_photos") && <Note key="p" tone="warn" icon={Camera} title="Bez fotek" testid="flag-no_photos">{data.photos_required ? "Fotky jsou u tohoto pracovníka povinné." : null}</Note>,
    flags.includes("self_declared") && <Note key="d" tone="info" icon={Pencil} title="Konec doplnil pracovník" testid="flag-self_declared" />,
    daySheet && <Note key="v" tone="info" icon={BookOpen} title="Z denního výkazu" testid="shift-note-daysheet">Schvaluje se celý výkaz za den, hodiny se upravují ve výkazu.</Note>,
    data.source === "diary" && <Note key="dn" tone="info" icon={BookOpen} title="Zapsáno v deníku" testid="shift-note-diary">Hodiny se upravují v partě deníku.</Note>,
    data.manual && data.source !== "diary" && !daySheet && <Note key="m" tone="info" icon={Pencil} title="Zapsáno ručně" testid="shift-note-manual" />,
    ...(data.corrections || []).map((c, i) => (
      <Note key={`c${i}`} tone="warn" icon={Pencil} testid={`shift-correction-${i}`}
        title={`Upravil ${c.user_name || c.by_name || "?"}${c.at ? ` · ${formatDate(c.at)} ${formatTime(c.at)}` : ""}`}>
        {[corrText(c), c.note ? `„${c.note}“` : ""].filter(Boolean).join(" · ")}
      </Note>
    )),
  ].filter(Boolean) : [];

  const inSub = data?.manual ? "ručně zadáno" : data?.source === "diary" ? "zapsáno v deníku" : daySheet ? "z denního výkazu"
    : ci.distance_m != null ? (
      <button type="button" onClick={() => setTab("place")} data-testid="shift-place-link"
        className={`inline-flex items-center gap-1 hover:underline ${ci.out_of_radius ? "text-destructive font-semibold" : "text-success font-semibold"}`}>
        <MapPin size={13} />aplikace · {ci.distance_m} m od stavby
      </button>
    ) : "aplikace · bez GPS";
  const outSub = !finished ? "směna ještě běží" : data?.auto_closed_at ? `odhlásil systém v ${hhmm(pragueTime(data.auto_closed_at))}`
    : data?.manual ? "ručně zadáno" : data?.source === "diary" ? "zapsáno v deníku"
    : co.distance_m != null ? `aplikace · ${co.distance_m} m od stavby` : "aplikace";
  const tv = (k) => (
    <>
      <span className={form[k] !== o[k] && editable ? "text-brand" : ""} data-testid={`shift-${k}-read`}>{hhmm(form[k]) || "—"}</span>
      {editable && form[k] !== o[k] && <s className="text-xs font-medium text-muted-foreground">{hhmm(o[k])}</s>}
    </>
  );
  const status = approved ? ["Schváleno", "text-success bg-success/10"] : !finished ? ["Běží", "text-brand bg-brand/10"] : ["Ke schválení", "text-brand bg-brand/10"];
  const badge = approved ? ["bg-success", Check] : flags.includes("out_of_radius") ? ["bg-destructive", MapPin] : ["bg-brand", Clock];
  const BadgeIcon = badge[1];
  const kv = (l, v, testid) => (
    <div className="flex justify-between gap-4 py-2.5 text-[13.5px] [&+&]:border-t border-border/60" data-testid={testid}>
      <span className="text-muted-foreground shrink-0">{l}</span><b className="font-semibold text-right min-w-0 break-words">{v}</b>
    </div>
  );
  const srcText = daySheet ? "Denní výkaz" : data?.source === "diary" ? "Deník" : data?.manual ? "Ručně" : "Aplikace v telefonu";

  const top = data && (
    <div className="flex items-center gap-2" data-testid="shift-review-top">
      {finished && (
        <ApproveButton label={approved ? "Schváleno" : dirty ? "Upravit a schválit" : daySheet ? "Schválit výkaz" : "Schválit"}
          done={approved} disabled={busy} onClick={approved ? unapprove : approve}
          title={approved ? (daySheet ? "Zrušit schválení celého denního výkazu" : "Zrušit schválení — směna se odemkne") : daySheet ? "Schválí celý denní výkaz za tento den" : undefined}
          testid={approved ? "shift-approved" : "shift-approve"} />
      )}
      <span className="flex-1" />
    </div>
  );

  return (
    <>
    <ReviewPanel open={!!sid} onClose={onClose} nav={nav} dirty={dirty} testid="shift-review-dialog" title="Směna" glass={false}
      mobileClass="max-w-3xl [&>*]:min-w-0" onInteractOutside={(e) => { if (delPhoto || confirmDel || pinView) e.preventDefault(); }}>
      {(controls) => (loading || !data ? (
          <div className="p-6 space-y-3" data-testid="shift-review-loading">
            <div className="flex items-start justify-between gap-2"><div className="h-9 w-28 rounded-[10px] bg-muted animate-pulse motion-reduce:animate-none" />{controls}</div>
            <div className="h-40 rounded-[18px] bg-muted animate-pulse motion-reduce:animate-none" />
          </div>
        ) : (
          <>
            {/* Nahoře: Schválit vlevo (jako úkoly a faktury), vpravo ⋮ a 2 / 5 ↑ ↓ ✕ */}
            <div className="pl-4 sm:pl-6 pr-14 md:pr-5 pt-1 md:pt-4 pb-2 flex items-center gap-2">
              <div className="flex-1 min-w-0">{top}</div>{menu}{controls}
            </div>

            <div className="overflow-y-auto overscroll-contain px-4 sm:px-6 pb-6 space-y-3.5 min-w-0">
              {/* Účtenka: kdo, velké číslo hodin, stav, celkem za den */}
              <div className="grid justify-items-center text-center gap-1 pt-1">
                <span className="relative">
                  <Avatar name={name} size={64} className="bg-brand/10 text-brand" />
                  <span className={`absolute -right-1 -bottom-1 h-6 w-6 rounded-full grid place-items-center text-white border-[3px] border-card ${badge[0]}`}><BadgeIcon size={12} /></span>
                </span>
                <p className="mt-1.5 text-[15px] font-bold min-w-0 max-w-full truncate">
                  <span data-testid="shift-review-name">{name}</span>
                  <span className="text-muted-foreground font-semibold"> · {data.user.position || roleLabel(data.user.role)}</span>
                </p>
                <p className="text-[13px] text-muted-foreground max-w-full truncate">{data.project.name}{data.rate_kind === "subcontract" ? " · subdodávka, bez mzdy" : ""}</p>
                <p className={`text-[44px] sm:text-[52px] leading-none font-black tracking-tight tabular-nums mt-1 ${dirty ? "text-brand" : ""}`} data-testid="shift-hours">{formatHours(liveHours)}</p>
                <p className="text-[13.5px] text-muted-foreground tabular-nums">
                  {dayLabel(data.date)} · {hhmm(form.in)}–{finished ? hhmm(form.out) : "teď"} · {brkMin ? `přestávka ${brkMin} min` : "bez přestávky"}
                </p>
                <span className={`mt-0.5 text-[12px] font-bold rounded-full px-2.5 py-1 ${status[1]}`} data-testid="shift-status">{status[0]}</span>
                {others.length > 0 && (
                  <p className="text-[12.5px] text-muted-foreground mt-0.5" data-testid="shift-day-total">
                    celkem za den <b className="text-foreground">{formatHours(dayTotal)}</b> · i {others.map((x) => `${x.project_name} ${hhmm(pragueTime(x.check_in))}–${x.check_out ? hhmm(pragueTime(x.check_out)) : "teď"}`).join(", ")}
                  </p>
                )}
              </div>

              {/* Směna | Místo */}
              <div ref={autoPill} role="tablist" className="grid grid-cols-2 p-1 rounded-full bg-background dark:bg-secondary text-[14px] font-bold" data-testid="shift-tabs">
                {[["shift", "Směna"], ["place", <>Místo{gps.out_of_radius ? <AlertTriangle size={14} className="inline ml-1.5 -mt-0.5 text-amber-600 dark:text-amber-400" /> : null}</>]].map(([k, label]) => (
                  <button key={k} role="tab" type="button" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`shift-tab-${k}`}
                    className={`h-9 rounded-full transition-colors ${tab === k ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>{label}</button>
                ))}
              </div>

              {tab === "place" ? (
                <PresenceMap testid="shift-place" radius={gps.radius_m || 300}
                  sites={[{ ...(gps.site || {}), label: data.project.name }]}
                  points={[
                    { key: "in", letter: "P", color: "#1f8a5b", label: "Příchod", time: form.in || pragueTime(data.check_in),
                      lat: ci.lat, lng: ci.lng, distance_m: ci.distance_m, out: ci.out_of_radius, note: data.manual ? "ručně zadáno" : null },
                    { key: "out", letter: "O", color: "#c77a12", label: "Odchod", time: form.out || pragueTime(data.check_out),
                      lat: co.lat, lng: co.lng, distance_m: co.distance_m, out: co.out_of_radius, note: data.auto_closed_at ? "auto odhlášeno" : null },
                  ]} />
              ) : (<>
              {notes.length > 0 && <div className="space-y-2" data-testid="shift-notes">{notes}</div>}

              {/* Osa Příchod → Přestávka → Odchod → Schválení; tužka jen u neschválené ukončené směny */}
              <div className="rounded-[20px] bg-background dark:bg-secondary px-3.5 py-1" data-testid="shift-sec-time">
                <Step dot={ci.out_of_radius ? "bad" : "ok"} label="Příchod" sub={inSub} value={tv("in")} editKey="in"
                  onEdit={editable ? () => setEdit((k) => (k === "in" ? null : "in")) : null} open={edit === "in"}>
                  <TimeStepper value={form.in} onChange={(v) => { setForm((f) => ({ ...f, in: v })); setNeedWhy(false); }} label="Příchod" testid="shift-in" changed={form.in !== o.in} />
                  {form.in !== o.in && <span className="text-xs text-muted-foreground tabular-nums">bylo {hhmm(o.in)}</span>}
                </Step>
                <Step dot="ok" label="Přestávka" editKey="brk" value={<span className={editable && String(form.brk) !== String(o.brk) ? "text-brand" : ""} data-testid="shift-break-read">{brkMin} min</span>}
                  sub={form.brk === "" ? `automaticky${span > 360 ? " nad 6 h" : ""}` : brkMin ? "zadaná ručně" : "bez přestávky"}
                  onEdit={editable ? () => setEdit((k) => (k === "brk" ? null : "brk")) : null} open={edit === "brk"}>
                  <div className="flex flex-wrap gap-1.5" role="group" aria-label="Přestávka" data-testid="shift-break-chips">
                    {[["", `auto · ${span > 360 ? 30 : 0} min`], ...BREAKS.map((m) => [String(m), `${m} min`])].map(([v, l]) => (
                      <button key={v || "auto"} type="button" onClick={() => { setForm((f) => ({ ...f, brk: v })); setNeedWhy(false); }} aria-pressed={String(form.brk) === v}
                        data-testid={`shift-break-${v || "auto"}`}
                        className={`h-9 px-3 rounded-full border text-[13px] font-semibold tabular-nums ${String(form.brk) === v ? "bg-brand/10 border-brand text-brand" : "bg-card border-border hover:border-muted-foreground"}`}>{l}</button>
                    ))}
                    <Input type="number" inputMode="numeric" min={0} max={720} aria-label="Jiná přestávka v minutách" placeholder="jiná"
                      value={form.brk !== "" && !BREAKS.includes(Number(form.brk)) ? form.brk : ""} data-inline-edit data-testid="shift-break-custom"
                      onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, brk: v === "" ? o.brk : String(Math.max(0, Math.min(720, Number(v) || 0))) })); }}
                      className="h-9 w-20 rounded-full text-[16px] md:text-[13px] text-center" />
                  </div>
                </Step>
                <Step dot="ok" label="Odchod" sub={outSub} value={finished ? tv("out") : <span className="text-brand">běží</span>} editKey="out"
                  onEdit={editable ? () => setEdit((k) => (k === "out" ? null : "out")) : null} open={edit === "out"}>
                  <TimeStepper value={form.out} onChange={(v) => { setForm((f) => ({ ...f, out: v })); setNeedWhy(false); }} label="Odchod" testid="shift-out" changed={form.out !== o.out} />
                  {form.out !== o.out && <span className="text-xs text-muted-foreground tabular-nums">bylo {hhmm(o.out)}</span>}
                </Step>
                <Step dot={approved ? "ok" : "now"} label="Schválení" editKey="ok" last
                  value={approved ? <Check size={16} className="text-success" /> : null}
                  sub={approved ? `${data.approved_by_name || "schváleno"}${data.approved_at ? ` · ${formatDate(data.approved_at)}` : ""} · úpravy až po zrušení schválení`
                    : daySheet ? "schvaluje se celý denní výkaz" : !finished ? "po skončení směny" : "čeká na schválení"} />
              </div>

              {/* Důvod úpravy — povinný, pracovník ho uvidí */}
              {dirty && (
                <div className={`rounded-[16px] border-[1.5px] border-amber-500/70 bg-amber-500/[0.08] p-3.5 space-y-2.5 ${needWhy ? "ring-4 ring-amber-500/25" : ""}`} data-testid="shift-sec-reason">
                  <div><b className="font-extrabold">Proč to upravuješ? <span className="text-destructive">*</span></b>
                    <span className="block text-[13px] text-muted-foreground">{name} důvod uvidí a zůstane u směny.</span></div>
                  <p className="text-[13px] font-semibold tabular-nums" data-testid="shift-reason-changes">
                    {changes.join(" · ")}{Math.abs(liveHours - origHours) > 0.001 ? ` · ${formatHours(origHours)} → ${formatHours(liveHours)}` : ""}
                  </p>
                  <div className="flex flex-wrap gap-1.5">
                    {WHY.map((c) => (
                      <button key={c} type="button" onClick={() => { setWhy((w) => (w === c ? null : c)); setNeedWhy(false); }} aria-pressed={why === c} data-testid={`shift-why-${WHY.indexOf(c)}`}
                        className={`h-9 px-3 rounded-full border text-[13px] font-semibold ${why === c ? "bg-brand/10 border-brand text-brand" : "bg-card border-border hover:border-muted-foreground"}`}>{c}</button>
                    ))}
                  </div>
                  <Input value={whyText} onChange={(e) => { setWhyText(e.target.value); setNeedWhy(false); }} placeholder="Nebo napiš vlastní důvod"
                    className="h-11 w-full min-w-0 bg-card text-[16px] md:text-sm" data-testid="shift-reason" data-inline-edit />
                  <div className="flex flex-wrap justify-end gap-2">
                    <Button variant="ghost" onClick={discard} disabled={busy} className="h-10" data-testid="shift-discard">Zahodit</Button>
                    <Button variant="outline" onClick={saveEdits} disabled={busy} className="h-10 font-bold" data-testid="shift-save">Uložit</Button>
                    <Button onClick={approve} disabled={busy} className="h-10 font-bold" data-testid="shift-save-approve"><Check size={16} /> Uložit a schválit</Button>
                  </div>
                </div>
              )}

              {/* Na čem dělal */}
              <Section icon={Pencil} title="Na čem dělal" testid="shift-sec-report">
                {repEdit ? (
                  <div className="space-y-2">
                    <Textarea rows={3} value={form.report} autoFocus data-inline-edit onChange={(e) => setForm((f) => ({ ...f, report: e.target.value }))}
                      placeholder="Např. betonáž desky, montáž bednění…" className="w-full min-w-0 text-[16px] md:text-sm" data-testid="shift-report-edit" />
                    <div className="flex justify-end"><Button variant="outline" onClick={() => setRepEdit(false)} className="h-9" data-testid="shift-report-done">Hotovo</Button></div>
                  </div>
                ) : (
                  <button type="button" disabled={!editable} onClick={() => setRepEdit(true)} data-testid="shift-report"
                    aria-label={editable ? "Upravit popis práce" : undefined}
                    className={`w-full text-left rounded-[14px] bg-background dark:bg-secondary border border-transparent px-3.5 py-3 text-[14.5px] leading-relaxed break-words ${editable ? "hover:border-border cursor-text" : "cursor-default"} ${form.report !== o.report && editable ? "ring-2 ring-brand/30" : ""}`}>
                    {form.report?.trim() ? form.report : <span className="italic text-muted-foreground">Bez popisu.</span>}
                  </button>
                )}
              </Section>

              {/* Úkoly */}
              {(data.tasks_completed.length > 0 || data.tasks_worked.length > 0) && (
                <Section icon={ListChecks} title="Úkoly">
                  <div className="space-y-1.5" data-testid="shift-tasks">
                    {data.tasks_completed.map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-sm"><span className="text-success"><Check size={15} /></span><span className="truncate">{t.title}</span><span className="text-[11px] font-bold uppercase text-success bg-success/10 rounded-md px-1.5 py-0.5">dokončeno</span></div>
                    ))}
                    {data.tasks_worked.filter((t) => !data.tasks_completed.some((c) => c.id === t.id)).map((t) => (
                      <div key={t.id} className="flex items-center gap-2 text-sm text-muted-foreground"><span className="w-[15px]" /><span className="truncate">{t.title}</span><span className="text-[11px] font-bold uppercase bg-secondary rounded-md px-1.5 py-0.5">pracoval</span></div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Materiál */}
              {data.materials.length > 0 && (
                <Section icon={Package} title="Nahlášený materiál">
                  <div className="space-y-1" data-testid="shift-materials">
                    {data.materials.map((m, i) => (
                      <div key={i} className="flex justify-between gap-3 text-sm"><span className="truncate">{m.name}</span><span className="tabular-nums shrink-0">{m.qty} {m.unit}</span></div>
                    ))}
                  </div>
                </Section>
              )}

              {/* Fotky — přidat výběrem nebo přetažením, oko = do deníku */}
              <Section icon={Camera} testid="shift-sec-photos"
                onDragOver={(e) => { if (e.dataTransfer?.types?.includes("Files")) { e.preventDefault(); setDragOver(true); } }}
                onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
                onDrop={(e) => { if (e.dataTransfer?.files?.length) { e.preventDefault(); setDragOver(false); addPhotos(e.dataTransfer.files); } }}
                title={<>Fotky{photos.length ? ` · ${photos.length}` : ""}{data.photos_required && photos.length === 0 && <span className="ml-1.5 text-[11px] font-bold text-amber-600 dark:text-amber-400 bg-amber-500/10 rounded-md px-2 py-0.5" data-testid="shift-photos-missing">povinné, chybí</span>}</>}
                action={(canPinDefects || photos.some((p) => p.status !== "approved")) && (
                  <span className="flex items-center gap-3 shrink-0">
                    {canPinDefects && <button type="button" onClick={() => setPinView({ start: 0 })} data-testid="shift-defects-pin" className="inline-flex items-center gap-1 text-[13px] font-bold text-brand hover:underline"><MapPin size={14} /> Závada</button>}
                    {photos.some((p) => p.status !== "approved") && <button type="button" onClick={approveAllPhotos} data-testid="shift-photos-approve-all" className="text-[13px] font-bold text-brand hover:underline">Vše do deníku</button>}
                  </span>
                )}>
                <div className="grid grid-cols-3 md:grid-cols-4 gap-2" data-testid="shift-photos">
                  {photos.map((p) => {
                    const inDiary = p.status === "approved";
                    return (
                      <div key={p.id} data-testid={`shift-photo-${p.id}`} className="relative aspect-square rounded-[12px] overflow-hidden border border-border">
                        <button type="button" onClick={() => setPinView({ start: photos.indexOf(p) })} className="w-full h-full" aria-label="Zvětšit fotku"><SignedImg url={p.url} alt="" thumb={200} className="w-full h-full object-cover" /></button>
                        <div className="absolute top-1.5 right-1.5 flex gap-1">
                          <button type="button" onClick={() => toggleEye(p)} data-testid={`shift-photo-eye-${p.id}`} title={inDiary ? "V deníku" : "Skryto z deníku"} aria-label={inDiary ? "V deníku, skrýt" : "Skrytá, pustit do deníku"}
                            className={`w-8 h-8 rounded-full grid place-items-center ${inDiary ? "bg-success text-white" : "bg-black/45 text-white/85"}`}>{inDiary ? <Eye size={15} /> : <EyeOff size={15} />}</button>
                          <button type="button" onClick={() => setDelPhoto(p)} data-testid={`shift-photo-del-${p.id}`} title="Smazat fotku" aria-label="Smazat fotku"
                            className="w-8 h-8 rounded-full grid place-items-center bg-black/45 text-white/85 hover:bg-destructive"><Trash2 size={14} /></button>
                        </div>
                        <span className="absolute bottom-1.5 left-1.5 text-[10px] font-bold px-1.5 py-0.5 rounded-full" style={{ background: inDiary ? "rgba(34,140,79,.9)" : "rgba(40,40,44,.85)", color: "#fff" }}>{inDiary ? "v deníku" : "skrytá"}</span>
                      </div>
                    );
                  })}
                  {Array.from({ length: uploading }).map((_, i) => (
                    <div key={`u${i}`} className="aspect-square rounded-[12px] border border-border bg-secondary grid place-items-center" data-testid="shift-photo-uploading"><Loader2 size={20} className="animate-spin motion-reduce:animate-none text-muted-foreground" /></div>
                  ))}
                  <label data-testid="shift-photo-add" title="Vyber fotky nebo je sem přetáhni"
                    className={`aspect-square rounded-[12px] border-[1.5px] border-dashed grid place-content-center justify-items-center gap-1 p-2 text-center text-[12px] font-semibold cursor-pointer transition-colors focus-within:ring-2 focus-within:ring-brand/40 ${dragOver ? "border-brand bg-brand/10 text-brand" : "border-muted-foreground/40 text-muted-foreground hover:border-brand hover:text-brand"}`}>
                    <ImagePlus size={20} />
                    <span><span className="md:hidden">Přidat fotky</span><span className="hidden md:inline">Přidat fotky nebo přetáhni sem</span></span>
                    <input ref={fileRef} type="file" accept="image/*,.heic,.heif" multiple className="sr-only" data-testid="shift-photo-input" onChange={(e) => addPhotos(e.target.files)} />
                  </label>
                </div>
              </Section>

              <DefectTaskForm ref={defRef} drafts={drafts} setDrafts={setDrafts} highlight={defFlash}
                projectId={data.project?.id} projectName={data.project?.name} assignee={data.user} sourceKind="attendance" sourceId={data.id}
                onEditDraft={(k) => setPinView({ start: Math.max(0, photoUrls.indexOf(drafts[k].photo_url)), focusDraft: k })}
                note="Docházku schválíte zvlášť. Úkol dostane fotky se špendlíky a popisy." />

              {/* Souhrn */}
              <div className="rounded-[18px] bg-background dark:bg-secondary px-3.5 py-0.5" data-testid="shift-summary">
                {kv("Zakázka", data.project.name)}
                {kv("Zdroj", srcText)}
                {canSeeMoney && rate != null && kv("Sazba", `${formatCZK(Math.round(rate))}/h`)}
                {canSeeMoney && liveCost != null && kv("Za den", formatCZK(liveCost), "shift-cost")}
              </div>

              {/* Komentáře — jen zprávy pracovníkovi, bez příchodů a odchodů */}
              <Section icon={MessageSquare} title={`Komentáře${comments.length ? ` · ${comments.length}` : ""}`} testid="shift-comments">
                {comments.length > 0 && (
                  <ul className="space-y-3">
                    {comments.map((c) => (
                      <li key={c.id} className="grid grid-cols-[28px_minmax(0,1fr)] gap-2.5" data-testid="shift-comment">
                        <Avatar name={c.user_name} size={28} className="bg-secondary text-muted-foreground" />
                        <div className="min-w-0 text-[13.5px]"><b className="font-bold">{c.user_name}</b><span className="text-xs text-muted-foreground ml-1.5">{c.at ? `${formatDate(c.at)} ${formatTime(c.at)}` : ""}</span>
                          <p className="mt-1 rounded-[12px] bg-background dark:bg-secondary px-3 py-2 text-[14px] break-words whitespace-pre-wrap">{c.text}</p></div>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex items-end gap-2 rounded-[14px] border border-border bg-card pl-3 pr-1.5 py-1.5 focus-within:border-brand">
                  <textarea rows={1} value={cm} onChange={(e) => setCm(e.target.value)} data-inline-edit data-testid="shift-comment-input"
                    onKeyDown={(e) => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); sendComment(); } }}
                    placeholder={`Napiš komentář, ${name} ho uvidí`} aria-label="Komentář ke směně"
                    className="flex-1 min-w-0 resize-none bg-transparent py-1.5 text-[16px] md:text-sm outline-none" />
                  <button type="button" onClick={sendComment} disabled={!cm.trim()} aria-label="Odeslat komentář" data-testid="shift-comment-send"
                    className="h-9 w-9 shrink-0 rounded-[10px] grid place-items-center text-muted-foreground hover:text-brand disabled:opacity-40"><Send size={17} /></button>
                </div>
              </Section>
              </>)}
            </div>
          </>
        ))}
    </ReviewPanel>

    <AlertDialog open={!!delPhoto} onOpenChange={(v) => { if (!v) setDelPhoto(null); }}>
      <AlertDialogContent data-testid="shift-del-photo-dialog">
        <AlertDialogHeader><AlertDialogTitle>Smazat fotku?</AlertDialogTitle><AlertDialogDescription>Zmizí i z galerie.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => setDelPhoto(null)} className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={doDelPhoto} className="h-11 w-full sm:w-auto font-bold" data-testid="shift-del-photo-confirm">Smazat</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <AlertDialog open={confirmDel} onOpenChange={(v) => { if (!v) setConfirmDel(false); }}>
      <AlertDialogContent data-testid="shift-del-record-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>{data?.source === "diary" ? "Odstranit z deníku?" : "Smazat záznam docházky?"}</AlertDialogTitle>
          <AlertDialogDescription>{data?.source === "diary" ? `${name} zmizí z party i z docházky za tento den.` : "Tato akce je nevratná."}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <Button variant="outline" onClick={() => setConfirmDel(false)} className="h-11 w-full sm:w-auto">Zpět</Button>
          <Button variant="destructive" onClick={delRecord} disabled={busy} className="h-11 w-full sm:w-auto font-bold" data-testid="shift-del-record-confirm">{data?.source === "diary" ? "Odstranit" : "Smazat záznam"}</Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <PhotoPinViewer open={!!pinView} onClose={() => setPinView(null)} photos={photoUrls} start={pinView?.start || 0}
      focusDraft={pinView?.focusDraft ?? null} title={data ? `${data.user.name} · ${data.project.name}` : ""}
      drafts={drafts} setDrafts={setDrafts} editable={canPinDefects} onDone={showDefectForm}
      info={(url) => { const p = photos.find((x) => x.url === url); return p ? { author: p.author_name || data?.user?.name, time: p.created_at ? formatTime(p.created_at) : "", src: "docházka" } : null; }}
      diary={{ isIn: (url) => photos.find((x) => x.url === url)?.status === "approved", toggle: (url) => { const p = photos.find((x) => x.url === url); if (p) toggleEye(p); } }}
      onShare={canLeadTasks(user) ? (url) => { const p = photos.find((x) => x.url === url); if (p) shareToWhatsApp([p.id]); } : undefined}
      onDelete={(url) => { const p = photos.find((x) => x.url === url); if (p) { setPhotos((ps) => ps.filter((x) => x.id !== p.id)); api.delete(`/photos/${p.id}`).catch((e) => { toast.error(formatApiError(e.response?.data?.detail)); loadReview(data.id, true); }); } }} />
    </>
  );
}
